'use strict';

import store from 'store';
import BoardStore from '@stores/BoardStore';
import BoardActions from '@actions/BoardActions';
import { createNewDocument, updateCachedDocuments } from '@utils/Content';
import { deleteVirtualPlan } from '@utils/Plans';

/**
 * Plan Utilities
 *
 * Centralized utilities for managing meal plans, including:
 * - UUID resolution for virtual/saved plans
 * - Favorites management (add/remove from boards)
 * - Virtual plan persistence
 */

/**
 * Get the resolved UUID for a plan, accounting for virtual plans that were saved
 *
 * Virtual plans that get saved to the database receive a new UUID from the API.
 * This mapping is stored in local storage under 'virtual-menus' as:
 * { [virtualUuid]: { replaced_by: realUuid } }
 *
 * @param {Object} plan - The plan object
 * @returns {string} The real UUID to use (either original or replaced_by)
 */
export const getResolvedPlanUuid = (plan) => {
    if (!plan?.uuid) {
        return null;
    }

    // If plan has API links, it's already saved - use its UUID directly
    if (plan.links?.self) {
        return plan.uuid;
    }

    // Virtual plan - check if it was saved and has a real UUID mapping
    const virtualPlans = store.get('virtual-menus') || {};
    const virtualPlan = virtualPlans[plan.uuid];

    return virtualPlan?.replaced_by || plan.uuid;
};

/**
 * Add a plan to the user's favorites board
 *
 * @param {Object} plan - The plan to add to favorites
 * @param {Object} patient - Optional patient data to include with the favorite
 */
export const addPlanToFavorites = (plan, patient = null) => {
    const defaultBoard = BoardStore.getDefaultBoard();
    const newItem = {
        resource_id: plan.uuid,
        resource_type: plan.type || 'plan',
    };

    // Include patient profile if provided
    if (patient) {
        newItem.profile = patient;
    }

    if (defaultBoard.links) {
        // Board already exists in the API, add to it
        BoardActions.addToBoard(defaultBoard, [newItem]);
    } else {
        // Virtual board - create it
        defaultBoard.contents = defaultBoard.contents || [];
        defaultBoard.contents.push(newItem);
        BoardActions.upsertBoards([defaultBoard]);
    }
};

/**
 * Remove a plan from all boards (favorites)
 *
 * @param {Object} plan - The plan to remove from favorites
 */
export const removeFromFavorites = (plan) => {
    const boards = BoardStore.getBoardsByResourceId(plan.uuid);
    boards.forEach(board => {
        const itemsToRemove = board.contents.filter(
            content => content.resource_id === plan.uuid
        );
        if (itemsToRemove.length > 0) {
            BoardActions.removeFromBoard(board, itemsToRemove);
        }
    });
};

/**
 * Save a virtual plan to the database
 *
 * Converts a virtual (local-only) plan into a persisted plan by:
 * 1. Creating a new document in the API
 * 2. Updating the virtual-menus mapping
 * 3. Updating the document cache
 * 4. Cleaning up local edit state
 *
 * @param {Object} virtualPlan - The virtual plan to save
 * @returns {Promise<Object>} Promise resolving to { success: boolean, plan?: Object, error?: string }
 */
export const saveVirtualPlan = async (virtualPlan) => {
    try {
        // Extract API-managed fields that shouldn't be sent in creation request
        // eslint-disable-next-line no-unused-vars
        const { uuid, links, created, type, status, merchant, ...planData } = virtualPlan;

        // Create new document in the API
        const savedPlan = await createNewDocument('plan', planData, 'plan/1');

        // Update virtual plan mapping with the real UUID
        deleteVirtualPlan(virtualPlan.uuid, savedPlan.uuid);

        // Clean up local edit state
        store.remove('menu-edited-' + virtualPlan.uuid);

        // Update document cache for immediate availability
        updateCachedDocuments([savedPlan]);

        return {
            success: true,
            plan: savedPlan
        };
    } catch (error) {
        return {
            success: false,
            error: error.message || 'Failed to save plan'
        };
    }
};
